/**
 * Family Fuel: one family-scoped shared fun-money envelope.
 *
 * Refilled on a cadence, spent down by logged family purchases, visible to
 * every member of the household. Deliberately **one** envelope — never
 * income, rent, groceries, or anything that would turn this into a household
 * budgeting app. See CODING_PLAN §14.
 *
 * Refill is period-derived exactly like allowance (`allowance.js`), reusing
 * `periodsBetween`/`periodStart` from `./periods.js`. There is no stored
 * `next_run`: a family that doesn't open the app for a month catches up to
 * the same balance a family that opened it daily would have, because every
 * refill entry's id is a pure function of (family, envelope, rule version,
 * period index) — never of when the job happened to run.
 *
 * Rollover is the feature that makes restraint visible: unspent Fuel moves
 * into a named family goal as an entry pair, not a recalculation, so the
 * transfer is itself an auditable event with its own id.
 *
 * Overdraw is visible, never clamped. A parent who spends past zero has
 * borrowed against the next refill, and the child sees that as a labelled
 * state, not a silently absorbed loss — the same honesty rule as everywhere
 * else in the ledger.
 */

import { dateFromDayNumber, dayNumber, periodIndexAt, periodsBetween, periodStart } from './periods.js';

export const ENVELOPE_REASON_TYPES = Object.freeze(['envelope_refill', 'envelope_spend', 'envelope_rollover']);

export const ENVELOPE_DEFAULTS = Object.freeze({
  version: 'fortunaut-envelope-v1',
  cadence: 'weekly',
  amountCents: 0,
  rolloverCapCents: 0,
});

export const withEnvelopeDefaults = (rule = {}) => ({ ...ENVELOPE_DEFAULTS, ...rule });

export const refillEntryId = (familyId, envelopeId, rule, periodIndex) =>
  `envelope_refill:${familyId}:${envelopeId}:${withEnvelopeDefaults(rule).version}:${periodIndex}`;

/**
 * Every refill entry owed between two dates that isn't already recorded.
 *
 * Mirrors `dueAllowanceEntries` exactly, for the same reason: call this on
 * every app open, or once after six weeks away, and the id — not the clock —
 * decides what's new. A run keyed off "today" instead of the period index
 * would pay a family that opens the app late a single lump sum dated today,
 * which both under- and mis-dates every missed week; see
 * `envelope.test.js`'s catch-up test for the guard.
 */
export function refillEntries({ familyId, envelopeId, rule = ENVELOPE_DEFAULTS, fromDate, toDate, existingIds = new Set() }) {
  const cfg = withEnvelopeDefaults(rule);
  const seen = existingIds instanceof Set ? existingIds : new Set(existingIds);

  return periodsBetween(fromDate, toDate, cfg.cadence).flatMap((periodIndex) => {
    const id = refillEntryId(familyId, envelopeId, cfg, periodIndex);
    if (seen.has(id)) return [];
    if (cfg.amountCents <= 0) return [];

    return [{
      id,
      familyId,
      envelopeId,
      currency: 'usd',
      amountCents: cfg.amountCents,
      reasonType: 'envelope_refill',
      reasonId: `${cfg.version}:${periodIndex}`,
      periodIndex,
      occurredAt: periodStart(periodIndex, cfg.cadence),
      snapshot: {
        version: cfg.version,
        cadence: cfg.cadence,
        amountCents: cfg.amountCents,
      },
    }];
  });
}

/**
 * The envelope balance, summed from history like every other balance here.
 *
 * `balanceCents` may be negative. It is never clamped to zero — an overdrawn
 * envelope is a real, visible state (see `overdraw`), and clamping it would
 * make the gauge lie about money a parent already spent.
 */
export function projectEnvelope(entries) {
  let refilledCents = 0;
  let spentCents = 0;
  let rolledOverCents = 0;
  let reversedCents = 0;

  for (const e of entries) {
    if (e.currency !== 'usd') continue;
    // Goal money has left the envelope. Counting the arriving half here would
    // cancel the departing half and the gauge would never move.
    if (e.ledger === 'goal') continue;
    if (e.reasonType === 'envelope_refill') refilledCents += e.amountCents;
    else if (e.reasonType === 'envelope_spend') spentCents += e.amountCents;
    else if (e.reasonType === 'envelope_rollover') rolledOverCents += e.amountCents;
    // A correction is a new entry, never an edit — so it has to be summed like
    // one. Counting only the three "real" reason types would mean undoing a
    // mis-tapped $6 ice cream left the gauge $6 down forever, with a visible
    // correction in the history that moved no money. The ledger would be right
    // and the number the family reads would be wrong, which is the worst of
    // the available failures.
    else if (e.reasonType === 'reversal') reversedCents += e.amountCents;
  }

  const balanceCents = refilledCents + spentCents + rolledOverCents + reversedCents;
  return {
    balanceCents, refilledCents, spentCents, rolledOverCents, reversedCents,
    ...overdraw(balanceCents),
  };
}

/**
 * Everything the envelope is owed between two dates: refills and rollovers,
 * interleaved in the order they actually happened.
 *
 * The order is the whole difficulty. A period's leftover rolls into the goal at
 * the moment that period ends, which is *before* the next period's refill
 * lands — get it backwards and the new week's money rolls straight into the
 * goal and the family starts every week empty.
 *
 * Like `refillEntries`, this is a pure function of the period index, so it is
 * safe to call on every app open. A family that doesn't open the app for six
 * weeks gets the same six refills and five rollovers, in the same order, with
 * the same ids, as a family that opened it daily. Running it twice produces
 * nothing the second time — belt and braces: the ids are derived, and a
 * rollover of an already-emptied envelope is a rollover of zero.
 *
 * An overdrawn period rolls over nothing. You cannot contribute a deficit to a
 * beach trip, and the deficit stays where it is — carried into the next period
 * as borrowing, which is exactly what §14 says should happen.
 */
export function dueEnvelopeEntries({
  familyId, envelopeId, goalId, goalFromDate, rule = ENVELOPE_DEFAULTS,
  existing = [], fromDate, toDate,
}) {
  const cfg = withEnvelopeDefaults(rule);
  const periods = periodsBetween(fromDate, toDate, cfg.cadence);
  if (!periods.length) return [];

  const all = [...existing];
  const seen = new Set(existing.map((e) => e.id));
  const created = [];

  const add = (entry) => {
    if (seen.has(entry.id)) return;
    seen.add(entry.id);
    all.push(entry);
    created.push(entry);
  };

  // `goalFromDate` stops rollovers being *dated* before the goal existed —
  // inventing history a family never lived would make the log unreadable and
  // the goal's progress unexplainable.
  //
  // It does NOT ring-fence money banked before the goal was named. The first
  // rollover after a goal starts moves whatever is in the tank, including
  // anything that accumulated while there was nowhere for it to go. Splitting
  // that out would need per-period attribution of a single running balance,
  // which is a lot of machinery for one transition; the honest alternative is
  // to say so where a parent can see it before they name the goal, which is
  // what the UI does.
  const goalFrom = goalFromDate ? periodIndexAt(goalFromDate, cfg.cadence) : -Infinity;

  for (const periodIndex of periods) {
    // Guarded, never `continue`d: skipping the iteration would skip this
    // period's refill too, and the money would simply cease to exist. The
    // conservation assertion in the tests is what caught that.
    if (goalId && periodIndex > periods[0] && periodIndex - 1 >= goalFrom) {
      const closing = periodIndex - 1;
      // Dated the last day of the period it closes, not the first day of the
      // next one, so "what we didn't spend that week" sits inside that week.
      const occurredAt = dateFromDayNumber(dayNumber(periodStart(periodIndex, cfg.cadence)) - 1);
      const soFar = all.filter((e) => periodIndexAt(e.occurredAt, cfg.cadence) <= closing);
      rolloverEntries({
        familyId, envelopeId, goalId,
        balanceCents: projectEnvelope(soFar).balanceCents,
        rule: cfg, periodIndex: closing, occurredAt,
      }).forEach(add);
    }

    const start = periodStart(periodIndex, cfg.cadence);
    refillEntries({
      familyId, envelopeId, rule: cfg,
      fromDate: start, toDate: start, existingIds: seen,
    }).forEach(add);
  }

  return created;
}

/**
 * What a family goal has actually accumulated.
 *
 * A projection over the arriving half of every rollover pair, never a stored
 * `saved_cents` — the same rule as every other balance here. A counter can
 * disagree with its own history; a sum cannot.
 */
export function projectGoal(entries, goalId) {
  let savedCents = 0;
  for (const e of entries) {
    if (e.currency !== 'usd') continue;
    if (e.ledger !== 'goal') continue;
    if (goalId && e.goalId !== goalId) continue;
    savedCents += e.amountCents;
  }
  return { savedCents };
}

/**
 * Undoing a logged spend, honestly.
 *
 * Not a delete and not an edit. A parent who mis-taps gets a correction that
 * says so, sitting next to the thing it corrected — because the moment history
 * can be quietly rewritten to avoid a conversation, the gauge stops being
 * evidence and goes back to being dad's opinion (CODING_PLAN §14, "never
 * fudge it").
 *
 * The id is derived from the entry being reversed, so a double-tapped undo
 * reverses once. Two undos of the same spend cannot refund it twice.
 */
export function envelopeReversalEntry({ original, actorId, reason, occurredAt }) {
  if (!reason) throw new Error('a reversal needs a visible reason');
  if (original.reasonType === 'reversal') {
    throw new Error('cannot reverse a reversal; log a new entry instead');
  }
  return {
    id: `reversal:${original.id}`,
    familyId: original.familyId,
    envelopeId: original.envelopeId,
    currency: original.currency,
    amountCents: -original.amountCents,
    reasonType: 'reversal',
    reversesId: original.id,
    actorId,
    note: reason,
    occurredAt,
  };
}

/**
 * A negative balance isn't an error state, it's borrowing from the next
 * refill — named and surfaced rather than silently absorbed. See CODING_PLAN
 * §14's "Overdraw is visible, never clamped."
 */
export function overdraw(balanceCents) {
  return { isOverdrawn: balanceCents < 0, borrowedCents: Math.max(0, -balanceCents) };
}

/** A logged family fun purchase. Always a negative movement against the envelope. */
export function spendEntry({ familyId, envelopeId, spendId, amountCents, tileId, note, actorId, occurredAt }) {
  if (!spendId) throw new Error('a spend needs a stable id');
  if (amountCents <= 0) throw new Error('spendEntry takes a positive amountCents; it negates it itself');

  return {
    id: `envelope_spend:${familyId}:${envelopeId}:${spendId}`,
    familyId,
    envelopeId,
    currency: 'usd',
    amountCents: -amountCents,
    reasonType: 'envelope_spend',
    reasonId: spendId,
    tileId,
    note,
    actorId,
    occurredAt,
  };
}

/**
 * Unspent Fuel rolling into a named family goal, as an entry pair rather
 * than a recalculation — one entry leaves the envelope, a second lands on
 * the goal, and their amounts are exact negatives of each other so nothing
 * is created or destroyed in the move.
 *
 * Nothing rolls when the balance isn't positive: a family that's borrowing
 * has nothing spare, and rolling a negative balance would either invent
 * money in the goal or double-count the deficit.
 *
 * `rule.rolloverCapCents` is a parent-set ceiling on how much a single
 * rollover can move, so the envelope can't quietly turn into a slush fund
 * that never gets spent down. Excess above the cap is left behind in the
 * envelope (not silently dropped) — it stays available for next period's
 * spending, which is the least surprising thing to do with fun money nobody
 * capped a goal to receive.
 */
export function rolloverEntries({ familyId, envelopeId, goalId, balanceCents, rule = ENVELOPE_DEFAULTS, periodIndex, occurredAt }) {
  if (balanceCents <= 0) return [];
  const cfg = withEnvelopeDefaults(rule);
  const cap = cfg.rolloverCapCents > 0 ? cfg.rolloverCapCents : Infinity;
  const moveCents = Math.min(balanceCents, cap);
  if (moveCents <= 0) return [];

  const reasonId = `${cfg.version}:${periodIndex}`;
  return [
    {
      id: `envelope_rollover_out:${familyId}:${envelopeId}:${goalId}:${periodIndex}`,
      familyId,
      envelopeId,
      currency: 'usd',
      amountCents: -moveCents,
      reasonType: 'envelope_rollover',
      reasonId,
      goalId,
      // Which ledger this entry is *in*. The pair spans two of them — money
      // leaves the envelope and arrives in the goal — and without saying so
      // explicitly a projection that sums both sides nets them to zero and the
      // rollover silently moves nothing. Same class of mistake as letting a
      // family entry reach a child's balance, and the same fix: say which
      // ledger it belongs to rather than inferring it from the sign.
      ledger: 'envelope',
      occurredAt,
      snapshot: { direction: 'out', rolloverCapCents: cfg.rolloverCapCents },
    },
    {
      id: `envelope_rollover_in:${familyId}:${envelopeId}:${goalId}:${periodIndex}`,
      familyId,
      goalId,
      currency: 'usd',
      amountCents: moveCents,
      reasonType: 'envelope_rollover',
      reasonId,
      envelopeId,
      ledger: 'goal',
      occurredAt,
      snapshot: { direction: 'in', rolloverCapCents: cfg.rolloverCapCents },
    },
  ];
}

/**
 * What a child sees before submitting a proposal: the gauge after the spend,
 * and how far out it pushes the family goal.
 *
 * The delay is derived from the refill rate alone — the same number that
 * decides how fast the goal fills in the first place — so the preview can't
 * drift from what actually happens once the spend is logged. `goal` is
 * optional: a family with no active goal still gets the gauge-after number.
 */
export function spendImpact({ balanceCents, amountCents, goal, refillCentsPerPeriod }) {
  const balanceAfterCents = balanceCents - amountCents;
  const impact = { balanceAfterCents, ...overdraw(balanceAfterCents) };

  if (!goal || !refillCentsPerPeriod) return impact;

  const remainingBeforeCents = Math.max(0, goal.targetCents - goal.savedCents);
  const remainingAfterCents = Math.max(0, goal.targetCents - goal.savedCents + amountCents);
  const periodsBefore = Math.ceil(remainingBeforeCents / refillCentsPerPeriod);
  const periodsAfter = Math.ceil(remainingAfterCents / refillCentsPerPeriod);

  return { ...impact, goalPeriodsDelay: periodsAfter - periodsBefore };
}

// --- SpendProposal: a small, pure state machine -----------------------------
//
// Five kids means a single shared pot rewards whoever asks loudest. A
// proposal can't reach a parent without a second from a *different* child —
// a second from the proposer themselves is not negotiation, it's a proposal
// wearing a second hat. See CODING_PLAN §14, "the commons problem is real."

export const PROPOSAL_STATUSES = Object.freeze([
  'proposed', 'seconded', 'approved', 'declined', 'countered',
]);

export function createProposal({
  id, familyId, envelopeId, childId, amountCents, tileId, note,
  requiresSecond = false,
}) {
  return {
    id, familyId, envelopeId, childId, amountCents, tileId, note,
    status: 'proposed',
    secondedBy: null,
    // Snapshotted, not read from settings at approval time — same rule as every
    // ledger entry. A parent who turns seconding on this afternoon has changed
    // how the *next* proposal works, not how the one their child filed this
    // morning works. Rules that change under an in-flight request are how a
    // child learns the process is arbitrary.
    requiresSecond,
  };
}

function assertStatus(proposal, expected) {
  if (proposal.status !== expected) {
    throw new Error(`cannot transition proposal ${proposal.id}: expected status "${expected}", got "${proposal.status}"`);
  }
}

/** A sibling — never the proposer — seconds the proposal before a parent ever sees it. */
export function secondProposal(proposal, siblingId) {
  assertStatus(proposal, 'proposed');
  if (siblingId === proposal.childId) {
    throw new Error('a proposal cannot be seconded by its own proposer');
  }
  return { ...proposal, status: 'seconded', secondedBy: siblingId };
}

/**
 * A proposal is decidable once it has whatever backing its own rule demanded.
 *
 * Seconding defaults **off**. It solves a real problem — one pot and five
 * claimants rewards whoever asks loudest — but it is a rule about how siblings
 * treat each other, and a family that hasn't met that problem yet would
 * experience it as the app inventing a hurdle. It earns its way on when a
 * parent recognises the situation it describes.
 *
 * A declined proposal needs no second either way: requiring a child to find a
 * co-signer before they can be told no is pure friction.
 */
function assertPending(proposal) {
  if (proposal.status !== 'proposed' && proposal.status !== 'seconded') {
    throw new Error(`proposal ${proposal.id} is already ${proposal.status}`);
  }
}

/** Approving and countering spend money, so they wait for the second. */
function assertBacked(proposal) {
  assertPending(proposal);
  if (proposal.status === 'proposed' && proposal.requiresSecond) {
    throw new Error(`proposal ${proposal.id} needs a sibling's second first`);
  }
}

export function approveProposal(proposal, actorId) {
  assertBacked(proposal);
  return { ...proposal, status: 'approved', decidedBy: actorId };
}

export function declineProposal(proposal, actorId, reason) {
  assertPending(proposal);
  return { ...proposal, status: 'declined', decidedBy: actorId, note: reason };
}

/** A parent proposes a different amount or tile instead of a flat yes/no. */
export function counterProposal(proposal, actorId, counter) {
  assertBacked(proposal);
  return { ...proposal, status: 'countered', decidedBy: actorId, counter };
}
